import * as React from 'react';
import { Col, Row } from 'antd';
import { observable } from 'mobx';
import { observer } from 'mobx-react';

import { TextView } from 'src/core/ui/text.view';
import { SearchOccupancyView } from 'src/app/bookings/search/search-occupancy.view';
import { DateView, DisableKind } from 'src/core/ui/date.view';
import { ProductServiceTypeEditorController } from 'src/app/products/product-service-types/parts/product-service-type-editor.controller';
import { ProductCategoryEditorController } from 'src/app/products/product-categories/parts/product-category-editor.controller';
import { SupplierEditorController } from 'src/app/products/suppliers/parts/supplier-editor.controller';
import { CurrencyEditorView } from 'src/app/general/currencies/parts/currency-editor.view';
import { CurrencyListDataService } from 'src/app/general/currencies/list/currency-list.data';
import { BooleanView } from '../../../core/ui/boolean.view';

interface ProductManuallyProps extends ComponentViewProps<ProductManually> {
	paxes: PersonOccupancy[];
	currency: string;
}

@observer
export class GeneralManuallyView extends React.Component<ProductManuallyProps, any> {
	private costCurrencyDataService: CurrencyListDataService;

	@observable
	costCurrency: CurrencySummary;

	constructor(props) {
		super(props);
		this.costCurrencyDataService = CurrencyListDataService.make();
		this.costCurrencyDataService.addSorting('iso3Name', 'asc', true);
	}

	private costCurrencyQuery = async (q?: string) => {
		this.props.setBusy(true);
		this.costCurrencyDataService.searchKeywords = q;
		try {
			const result = await this.costCurrencyDataService.load(query => query);
			return {
				items: result,
				totalCount: this.costCurrencyDataService.count
			};
		} catch (error) {
			throw error;
		} finally {
			this.props.setBusy(false);
		}
	}

	public renderProductManually(model: ProductManually, paxes: any[], occupancy: PersonOccupancy[]) {
		if (model.productType !== 0) {
			return null;
		}
		return (
			<React.Fragment>
				<Row gutter={16}>
					<Col xs={24} md={12}>
						<TextView
							required
							label="Product name"
							mode="edit"
							value={model.productDisplayName}
							onChanged={(newVal) => model.productDisplayName = newVal}
						/>

						<SupplierEditorController
							label="Supplier"
							required
							mode='edit'
							supplierType={0}
							onChange={(value: SupplierSummary) => {
								model.supplierId = value ? value.id : null;
								model.supplierTitle = value ? value.title : "";
								const currency = value
									? this.costCurrencyDataService.items.find(e => e.id === value.defaultCurrencyId)
									: undefined;
								this.costCurrency = currency;
							}}
							value={model.supplierId}
							setBusy={this.props.setBusy}
						/>

						<ProductCategoryEditorController
							required
							mode='edit'
							onChanged={(value) => model.productCategory = value}
							value={model.productCategory ? model.productCategory.id : undefined}
							productType={model.productType}
							setBusy={this.props.setBusy}
						/>

						<ProductServiceTypeEditorController
							required
							categoryId={model.productCategory ? model.productCategory.id : undefined}
							mode={model.productCategory != null ? 'edit' : 'display'}
							onChanged={(value) => model.productServiceType = value}
							value={model.productServiceType ? model.productServiceType.id : undefined}
							setBusy={this.props.setBusy}
						/>

						<BooleanView
							label="Requires confirmation"
							mode="edit"
							onChanged={e => model.requiresConfirmation = e}
							required
							value={model.requiresConfirmation}
						/>
					</Col>
					<Col xs={24} md={12}>

						<DateView
							required
							label="Start Date"
							mode="edit"
							value={model.dateFrom}
							onChanged={(newVal) => model.dateFrom = newVal}
							format="small-date"
							showTime={DisableKind.disableSeconds}
							dateUsage="utc"
						/>

						<DateView
							required
							label="End Date"
							mode="edit"
							value={model.dateTo}
							onChanged={(newVal) => model.dateTo = newVal}
							format="small-date"
							showTime={DisableKind.disableSeconds}
							dateUsage="utc"
						/>

						<CurrencyEditorView
							label='Cost Price Currency'
							mode="edit"
							required={true}
							onChange={(item) => {
								if (model.costPriceResult == null) {
									model.costPriceResult = {
										currencyIsoCode: '',
										exchangeRate: 1,
										isCommissionable: false,
										isPriceLive: true,
										sourceCurrencyIsoCode: '',
										totalPrice: model.costPrice,
										breakdown: [],
										paxs: model.occupancies.map(e => e.id),
									};
								}
								model.costPriceResult.currencyIsoCode = (item as CurrencySummary).iso3Name;
								this.costCurrency = item;
							}}
							value={this.costCurrency ? this.costCurrency.id : undefined}
							currencies={{ query: this.costCurrencyQuery, dataSet: this.costCurrencyDataService }}
							currencyArrayObjectType={false}
						/>

						<TextView
							required
							label="Cost Price"
							mode="edit"
							value={model.costPrice}
							onChanged={(newVal) => {
								model.costPrice = newVal;
								model.costPriceResult = {
									currencyIsoCode: model.costPriceResult.currencyIsoCode,
									exchangeRate: 1,
									isCommissionable: false,
									isPriceLive: true,
									sourceCurrencyIsoCode: model.costPriceResult.currencyIsoCode,
									totalPrice: model.costPrice,
									breakdown: [
										{
											exchangeRate: 1,
											percent: false,
											price: model.costPrice,
											priceType: "PerUnit",
											unitPrice: model.costPrice,
										},
									],
									paxs: model.occupancies.map(e => e.id),
								}
							}}
							suffix={this.costCurrency ? this.costCurrency.iso3Name : ""}
						/>

						<TextView
							required
							label="Sell Price"
							mode="edit"
							value={model.sellPrice}
							onChanged={(newVal) => {
								model.sellPrice = newVal;
								model.sellPriceResult = {
									currencyIsoCode: model.sellPriceResult.currencyIsoCode,
									exchangeRate: 1,
									isCommissionable: false,
									isPriceLive: true,
									sourceCurrencyIsoCode: model.sellPriceResult.currencyIsoCode,
									totalPrice: model.sellPrice,
									breakdown: [
										{
											exchangeRate: 1,
											percent: false,
											price: model.sellPrice,
											priceType: "PerUnit",
											unitPrice: model.sellPrice,
										},
									],
									paxs: model.occupancies.map(e => e.id),
								}
							}}
							suffix={this.props.currency}
						/>
					</Col>
				</Row>
				<SearchOccupancyView
					type="General"
					occupancies={[{ unit: 1, paxs: occupancy }]}
					setBusy={this.props.setBusy}
					paxs={paxes}
					lockUnits={true}
				/>
			</React.Fragment>
		);
	}

	public render() {
		if (this.props.model == null) {
			return null;
		}
		return this.renderProductManually(this.props.model, this.props.paxes, this.props.model.occupancies)
	}
}



// WEBPACK FOOTER //
// ./src/app/bookings/product-manually/general-manually.view.tsx